import { useCallback, useEffect, useState } from 'react';
import { proService } from '../services';
import api from '../services/api';
import { INK, MUTED, SUBTLE, INSET, RED, RULE, CYAN, PillButton, useArmed } from './club/clubUi';
import { Card, CardHead, TextInput, Inset, fieldLabel } from './pro/setup/setupUi';

/** Origin of the API host (api.partyresume.com), which is where the .ics feed actually lives. */
const apiOrigin = () => { try { return new URL(api.defaults.baseURL, window.location.origin).origin; } catch { return window.location.origin; } };

/**
 * CONNECTED CALENDARS (design_handoff_pro_setup "AVAILABILITY"): connect the calendar a Pro already
 * lives in, so their real life blocks their availability without them maintaining two calendars.
 *
 * Every calendar worth connecting publishes an iCalendar feed — Google, Apple, Outlook, Calendly —
 * so one paste box covers all of them. The address is a secret: anyone holding a Google "secret
 * address in iCal format" can read that whole calendar. It's sent once and never returned — the
 * list shows only a hint of it. Everything here writes through at once; nothing waits for SAVE.
 */
const WHERE_TO_FIND = [
  ['Google Calendar', 'calendar.google.com → hover the calendar → ⋮ → Settings and sharing → Integrate calendar → “Secret address in iCal format”'],
  ['Apple (iCloud calendars only)', 'Control-click the calendar under iCloud → Share Calendar… → tick Public Calendar → Copy Link'],
  ['Outlook / Microsoft 365', 'Settings → Calendar → Shared calendars → Publish a calendar → ICS link'],
];

export default function CalendarFeedManager({ onChanged, phone = false, say }) {
  const [feeds, setFeeds] = useState([]);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [outbound, setOutbound] = useState(null);
  const [copied, setCopied] = useState(false);
  const [help, setHelp] = useState(false);
  const { armed, confirm, disarm } = useArmed(5000);

  const load = useCallback(async () => {
    const r = await proService.listCalendarFeeds();
    if (r?.success) setFeeds(r.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const validUrl = /^(https?|webcal):\/\//i.test(url.trim());
  const add = async (e) => {
    e?.preventDefault?.();
    if (!validUrl || busy) return;
    setBusy(true); setError('');
    // The offset travels with the feed: a 5pm Pacific event is midnight UTC the next day, so
    // without it we'd block the wrong date entirely.
    const r = await proService.addCalendarFeed(url.trim(), label.trim() || null, new Date().getTimezoneOffset());
    setBusy(false);
    if (!r?.success) { setError(r?.error || 'Could not read that calendar.'); return; }
    setUrl(''); setLabel('');
    say?.('CALENDAR CONNECTED');
    load();
    onChanged?.();
  };
  // Every one of these moves busy time around, so the calendar above has to be told.
  const sync = async (f) => { setBusy(true); await proService.syncCalendarFeed(f.id); setBusy(false); say?.(`SYNCED · ${(f.label || f.provider || '').toUpperCase()}`); load(); onChanged?.(); };
  const remove = async (f) => {
    if (!confirm(`rm-${f.id}`)) return;
    setBusy(true); await proService.removeCalendarFeed(f.id); setBusy(false);
    say?.('CALENDAR REMOVED'); load(); onChanged?.();
  };
  const showOutbound = async () => {
    const r = await proService.bookingFeedUrl();
    // Served by the API host, not the SPA host — partyresume.com/api answers with the app shell.
    if (r?.success) setOutbound(`${apiOrigin()}${r.data.url}`);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(outbound); } catch { /* the field stays selectable */ }
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Card phone={phone} style={{ gap: 12 }}>
      <CardHead title="CONNECTED CALENDARS" right="BUSY TIMES BLOCK YOU OUT" />
      {feeds.length === 0 && <Inset>No calendars connected.</Inset>}
      {feeds.map((f) => {
        const isArmed = armed === `rm-${f.id}`;
        return (
          <div key={f.id} className="flex flex-wrap items-center" style={{ gap: 10, padding: '12px 12px 12px 18px', borderRadius: 20, background: INSET }}>
            <span className="flex min-w-0 flex-col" style={{ flex: '1 1 220px', gap: 3 }}>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', overflowWrap: 'anywhere', textTransform: 'uppercase' }}>{f.label || f.provider}</span>
              <span style={{ fontSize: 10, color: MUTED, overflowWrap: 'anywhere' }}>{f.urlHint}</span>
              <span style={{ fontSize: 10, color: f.lastStatus === 'error' ? RED : SUBTLE }}>
                {f.lastStatus === 'error' ? `Last sync failed — ${f.lastError || 'could not read it'}` : f.lastSyncedAt ? `${f.lastEventCount} event${f.lastEventCount === 1 ? '' : 's'} · synced ${new Date(f.lastSyncedAt).toLocaleString()}` : 'Not synced yet'}
              </span>
            </span>
            <PillButton size={36} fontSize={9} onClick={() => sync(f)} disabled={busy}>Sync</PillButton>
            <PillButton variant={isArmed ? 'armed' : 'dangerGhost'} size={36} fontSize={9} onClick={() => remove(f)} disabled={busy}>{isArmed ? 'Remove it' : 'Remove'}</PillButton>
            {isArmed && <PillButton variant="ghost" size={36} fontSize={9} onClick={disarm}>Keep</PillButton>}
          </div>
        );
      })}
      <form onSubmit={add} className="flex flex-col" style={{ gap: 8, paddingTop: 6 }}>
        <TextInput h={46} value={url} onChange={(e) => { setUrl(e.target.value); setError(''); }} placeholder="Paste the iCal address (https:// or webcal://)" aria-label="iCal address" />
        <span className="flex" style={{ gap: 8 }}>
          <TextInput h={46} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optional) — Work, Personal" aria-label="Calendar label" style={{ flex: 1, minWidth: 0 }} />
          <PillButton as="button" size={46} type="submit" disabled={busy || !validUrl} style={{ opacity: validUrl ? 1 : 0.35 }}>{busy ? 'Reading…' : 'Connect'}</PillButton>
        </span>
        {error && <p className="m-0" style={{ fontSize: 10, color: RED }}>{error}</p>}
        <button type="button" onClick={() => setHelp((h) => !h)} className="self-start font-mono underline" style={{ height: 30, padding: 0, border: 'none', background: 'transparent', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer' }}>{help ? 'HIDE' : 'WHERE DO I FIND THIS?'}</button>
        {help && (
          <Inset style={{ color: '#374151', fontSize: 11 }}>
            {WHERE_TO_FIND.map(([name, where]) => <span key={name}><b>{name}:</b> {where}</span>)}
            <span>A Gmail account showing inside Apple Calendar is still a Google calendar — get its address from Google, not from Apple.</span>
          </Inset>
        )}
        <span style={{ fontSize: 11, lineHeight: 1.6, color: MUTED }}>Busy times block your availability automatically. Days you set by hand, and days already booked here, are never touched. The address is a key to reading that calendar — keep it private.</span>
      </form>
      <div className="flex flex-col" style={{ gap: 8, paddingTop: 14, borderTop: RULE }}>
        <span style={fieldLabel}>Portal bookings → your calendar</span>
        {outbound ? (
          <span className="flex items-center" style={{ gap: 8, padding: '6px 6px 6px 18px', borderRadius: 999, background: INSET }}>
            <input readOnly value={outbound} onFocus={(e) => e.target.select()} aria-label="Your booking feed address" className="font-mono" style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: 11, outline: 'none' }} />
            <button type="button" onClick={copy} className="font-mono" style={{ height: 36, padding: '0 16px', border: 'none', borderRadius: 999, background: copied ? CYAN : INK, color: copied ? INK : '#fff', fontSize: 9, fontWeight: 800, letterSpacing: '.14em', cursor: 'pointer' }}>{copied ? 'COPIED' : 'COPY'}</button>
          </span>
        ) : (
          <PillButton variant="tertiary" size={40} onClick={showOutbound} style={{ alignSelf: 'flex-start' }}>Get my feed address</PillButton>
        )}
        <span style={{ fontSize: 11, lineHeight: 1.6, color: MUTED }}>Subscribe to this in Google or Apple Calendar and every booking you take here shows up there. Keep it private — the address is the key.</span>
      </div>
    </Card>
  );
}
